import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { merge } from 'rxjs';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { UserService } from '../../../user/service/user.service';
import { ADDON_CODE } from '../../../user/models/payment-plan.model';
import { getValvurApiError } from '../../../../core/errors/parse-valvur-api-error';
import type { ParentDuty } from '../../models/parent-duty.model';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { InfoBannerComponent } from '../../../../shared/components/ui/info-banner/info-banner.component';
import { InitialsPipe } from '../../../../shared/pipes/initials.pipe';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { ParentFamilyContextService } from '../../../parent-family/parent-family-context.service';
import {
  FamilyKidsService,
  type FamilyChild,
} from '../../../parent-family/services/family-kids.service';
import {
  DUTY_RECURRENCE_OPTIONS,
  recurrenceIcon,
  type DutyRecurrenceFrequency,
} from '../../models/duty-recurrence.model';
import { FamilyDutyFeatureService } from '../../services/family-duty-feature.service';
import { DutyTemplateService } from '../../services/duty-template.service';
import { DUTY_PARENT_NOTE_MAX_LENGTH } from '../../constants/duty-parent-note.constants';

export interface AddDutyPrefill {
  title?: string;
  content?: string;
  parentNote?: string;
  requiresParentParticipation?: boolean;
}

export interface AddDutyDialogData {
  selectedKids?: string[];
  mode: DutyMode;
  duty?: ParentDuty;
  prefill?: AddDutyPrefill;
}

export interface CreateDutyPayload {
  sender: string;
  senderName: string;
  reciever: string;
  recieverName: string;
  title: string;
  content: string;
  rewardGold: number;
  rewardXp: number;
  parentNote?: string;
  recurrenceFrequency?: DutyRecurrenceFrequency;
  requiresParentParticipation?: boolean;
}

export interface UpdateDutyPayload {
  dutyId: string;
  title: string;
  content: string;
  rewardGold: number;
  rewardXp: number;
  parentNote?: string;
  recurrenceActive?: boolean;
}

export type DutyMode = 'new' | 'edit';

function readDutyApiError(error: unknown): ReturnType<typeof getValvurApiError> {
  const direct = getValvurApiError(error);
  if (direct) {
    return direct;
  }
  if (!error || typeof error !== 'object') {
    return null;
  }
  const record = error as Record<string, unknown>;
  for (const nested of [record['cause'], record['error'], record['original']]) {
    const parsed = getValvurApiError(nested);
    if (parsed) {
      return parsed;
    }
  }
  if (
    typeof record['errorCode'] === 'string' &&
    typeof record['message'] === 'string'
  ) {
    return {
      statusCode:
        typeof record['statusCode'] === 'number' ? record['statusCode'] : 400,
      errorCode: record['errorCode'],
      message: record['message'],
      ...(record['meta'] && typeof record['meta'] === 'object'
        ? { meta: record['meta'] as never }
        : {}),
    };
  }
  return null;
}

const DEFAULT_DUTY_REWARD_XP = 10;

@Component({
  selector: 'app-add-duty-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    TranslatePipe,
    InputFieldComponent,
    ParentButtonComponent,
    InfoBannerComponent,
    InitialsPipe,
  ],
  templateUrl: './add-duty-dialog.component.html',
  styleUrls: ['./add-duty-dialog.component.scss'],
})
export class AddDutyDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AddDutyDialogComponent>);
  private readonly dialogData = inject(MAT_DIALOG_DATA, {
    optional: true,
  }) as AddDutyDialogData;
  private readonly fb = inject(FormBuilder);
  #userService = inject(UserService);
  #family = inject(ParentFamilyContextService);
  #familyKids = inject(FamilyKidsService);
  #familyDuty = inject(FamilyDutyFeatureService);
  #dutyTemplates = inject(DutyTemplateService);
  #router = inject(Router);
  #cdr = inject(ChangeDetectorRef);
  private takeUntilDestroyed = getTakeUntilDestroyed();
  #formTick = signal(0);
  #initialKidSyncDone = false;

  readonly recurrenceOptions = DUTY_RECURRENCE_OPTIONS;
  readonly recurrenceIcon = recurrenceIcon;
  readonly parentNoteMaxLength = DUTY_PARENT_NOTE_MAX_LENGTH;

  dutyForm!: FormGroup;
  selectedKids = signal<string[]>(this.dialogData?.selectedKids || []);
  isSubmitting = signal(false);
  submitErrorKey = signal<string | null>(null);
  submitErrorParams = signal<Record<string, string | number> | null>(null);
  recurrenceFrequency = signal<DutyRecurrenceFrequency>('NONE');
  requiresParentParticipation = signal(false);
  stopRecurrence = signal(false);
  saveAsTemplate = signal(false);
  planHintRequested = signal(false);

  nameValidation = signal<Record<string, string>>({ required: '' });
  messageValidation = signal<Record<string, string>>({ required: '' });
  parentNoteValidation = signal<Record<string, string>>({ maxlength: '' });
  templateNameValidation = signal<Record<string, string>>({
    required: '',
    minlength: '',
  });

  readonly user = computed(() => this.#userService.user());
  readonly kids = this.#familyKids.children;
  readonly experiencePoolBalance = computed(
    () => this.#userService.experiencePoolBalance(),
  );
  readonly resolvedRewardXp = computed(() => {
    this.#formTick();
    return this.resolveRewardXp(this.dutyForm?.value?.rewardXp);
  });
  readonly totalXpCost = computed(() => {
    const perDuty = this.resolvedRewardXp();
    if (this.isEditMode()) return perDuty;
    return perDuty * Math.max(this.selectedKids().length, 1);
  });
  readonly hasInsufficientXp = computed(() => {
    const balance = this.experiencePoolBalance();
    if (balance == null) return false;
    return this.totalXpCost() > balance;
  });
  readonly isPoolLow = computed(() => {
    const balance = this.experiencePoolBalance();
    return balance != null && balance < 50;
  });
  readonly isEditMode = computed(() => this.dialogData?.mode === 'edit');
  readonly editingDuty = computed(() => this.dialogData?.duty);
  readonly assignedKid = computed(() => {
    const receiverId = this.editingDuty()?.reciever;
    if (!receiverId) return null;
    return this.kids().find((kid) => kid.id === receiverId) ?? null;
  });
  readonly editRecurrenceLabelKey = computed(() => {
    const freq = this.editingDuty()?.recurrenceFrequency;
    if (!freq || freq === 'NONE') return null;
    const option = this.recurrenceOptions.find((o) => o.id === freq);
    return option?.labelKey ?? null;
  });
  readonly canChangeRecurrence = computed(
    () => !this.isEditMode() || !this.editingDuty()?.read,
  );
  readonly showStopRecurrence = computed(
    () =>
      this.isEditMode() &&
      !!this.editingDuty()?.recurrenceFrequency &&
      this.editingDuty()?.recurrenceFrequency !== 'NONE',
  );
  readonly showFamilyDutyOption = computed(() => this.#familyDuty.enabled());
  readonly familyDutyAllowed = computed(() =>
    this.#userService.isQuotaCapabilityAllowed('duties.family'),
  );
  readonly recurrenceAllowed = computed(() =>
    this.#userService.isQuotaCapabilityAllowed('duties.recurrence'),
  );
  readonly routinesAllowed = computed(() =>
    this.#userService.isQuotaCapabilityAllowed('duties.routines'),
  );
  readonly showPlanNotice = computed(() => {
    if (this.isEditMode()) {
      return false;
    }
    return !!this.submitErrorKey() || this.planHintRequested();
  });
  readonly planNoticeTitle = computed(() => {
    const errorKey = this.submitErrorKey();
    if (errorKey === 'app.components.addDutyDialog.plan.quotaCreate') {
      return this.translate.instant(
        'app.components.addDutyDialog.plan.quotaCreateTitle',
      );
    }
    if (errorKey) {
      return this.translate.instant(
        'app.components.addDutyDialog.plan.quotaTitle',
      );
    }
    return this.translate.instant(
      'app.components.addDutyDialog.plan.limitsTitle',
    );
  });
  readonly planNoticeMessage = computed(() => {
    const errorKey = this.submitErrorKey();
    if (errorKey) {
      return this.translate.instant(errorKey, this.submitErrorParams() ?? {});
    }
    return this.translate.instant(
      this.showFamilyDutyOption()
        ? 'app.components.addDutyDialog.plan.limitsMessage'
        : 'app.components.addDutyDialog.plan.limitsMessageNoFamily',
    );
  });
  readonly showDutyBoost = computed(
    () =>
      this.submitErrorKey() ===
        'app.components.addDutyDialog.plan.quotaCreate' &&
      this.#userService.canPurchaseAddons(),
  );
  readonly planNoticeActionLabel = computed(() => {
    if (this.showDutyBoost()) {
      const addon = this.#userService.findAddon(ADDON_CODE.DUTY_BOOST_5);
      const count = addon?.grantAmount ?? 5;
      return this.translate.instant(
        'app.components.addDutyDialog.plan.buyDutyBoost',
        { count },
      );
    }
    return this.translate.instant('app.components.addDutyDialog.plan.viewPlans');
  });
  readonly boostLoading = signal(false);
  readonly showKidBulkActions = computed(() => this.kids().length >= 3);
  readonly selectedKidsCount = computed(() => this.selectedKids().length);
  readonly allKidsSelected = computed(() => {
    const children = this.kids();
    const selected = this.selectedKids();
    return children.length > 0 && selected.length === children.length;
  });
  readonly parentNoteLength = computed(() => {
    this.#formTick();
    const value = this.dutyForm?.get('parentNote')?.value;
    return typeof value === 'string' ? value.length : 0;
  });

  readonly canConfirm = computed(() => {
    this.#formTick();
    this.selectedKids();
    this.isSubmitting();
    this.saveAsTemplate();
    return (
      this.dutyForm.valid &&
      this.selectedKids().length > 0 &&
      !this.isSubmitting() &&
      !this.hasInsufficientXp()
    );
  });

  constructor(private translate: TranslateService) {
    this.initForm();
    effect(() => {
      this.kids();
      this.syncInitialKidSelection();
    });
  }

  ngOnInit(): void {
    void this.#familyDuty.whenReady().then(() => {
      this.prefillCreateMode();
      this.#formTick.update((n) => n + 1);
    });
    this.loadValidationMessages();
    this.prefillEditMode();
    merge(this.dutyForm.statusChanges, this.dutyForm.valueChanges)
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => this.#formTick.update((n) => n + 1));
    this.#formTick.update((n) => n + 1);
  }

  selectRecurrence(id: DutyRecurrenceFrequency): void {
    if (!this.canChangeRecurrence()) return;
    if (id !== 'NONE' && !this.recurrenceAllowed()) {
      this.planHintRequested.set(true);
      return;
    }
    this.recurrenceFrequency.set(id);
  }

  toggleFamilyDuty(): void {
    if (this.isEditMode() || !this.#familyDuty.isEnabled()) return;
    if (!this.familyDutyAllowed()) {
      this.planHintRequested.set(true);
      return;
    }
    this.requiresParentParticipation.update((v) => !v);
  }

  toggleSaveAsTemplate(): void {
    if (this.isEditMode()) return;
    if (!this.routinesAllowed()) {
      this.planHintRequested.set(true);
      return;
    }
    const next = !this.saveAsTemplate();
    this.saveAsTemplate.set(next);
    const control = this.dutyForm.get('templateName');
    if (next) {
      control?.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(80),
      ]);
    } else {
      control?.clearValidators();
      control?.setValue('');
    }
    control?.updateValueAndValidity({ emitEvent: true });
    this.#formTick.update((n) => n + 1);
  }

  toggleKidSelection(kidId: string): void {
    if (this.isEditMode()) return;
    const currentSelection = this.selectedKids();
    const isSelected = currentSelection.includes(kidId);
    if (isSelected) {
      this.selectedKids.set(currentSelection.filter((id) => id !== kidId));
    } else {
      this.selectedKids.set([...currentSelection, kidId]);
    }
  }

  isKidSelected(kidId: string): boolean {
    return this.selectedKids().includes(kidId);
  }

  selectAllKids(): void {
    if (this.isEditMode()) return;
    this.selectedKids.set(this.kids().map((kid) => kid.id));
  }

  clearKidSelection(): void {
    if (this.isEditMode()) return;
    this.selectedKids.set([]);
  }

  toggleAllKids(): void {
    if (this.allKidsSelected()) {
      this.clearKidSelection();
    } else {
      this.selectAllKids();
    }
  }

  getActiveDutiesCount(kid: FamilyChild): number {
    return kid.activeDutyCount ?? 0;
  }

  onDiscard(): void {
    this.dialogRef.close();
  }

  onCancelDuty(): void {
    const duty = this.editingDuty();
    if (!duty?.id || this.isSubmitting()) return;
    this.isSubmitting.set(true);
    this.#userService.cancelDuty.mutateAsync(duty.id).then(() => {
      this.isSubmitting.set(false);
      this.dialogRef.close({ cancelled: true });
    });
  }

  onConfirm(): void {
    if (!this.canConfirm()) return;

    this.isSubmitting.set(true);
    this.submitErrorKey.set(null);
    this.submitErrorParams.set(null);

    if (this.isEditMode()) {
      const duty = this.editingDuty();
      if (!duty?.id) return;
      const payload: UpdateDutyPayload = {
        dutyId: duty.id,
        title: this.dutyForm.value.title,
        content: this.dutyForm.value.content,
        parentNote: this.resolveParentNote(this.dutyForm.value.parentNote) ?? '',
        rewardGold: this.resolveRewardGold(this.dutyForm.value.rewardGold),
        rewardXp: this.resolveRewardXp(this.dutyForm.value.rewardXp),
      };
      if (this.showStopRecurrence() && this.stopRecurrence()) {
        payload.recurrenceActive = false;
      }
      this.#userService.updateDuty.mutate(payload, {
        onSuccess: () => {
          this.dialogRef.close(payload);
        },
        onError: (error: unknown) => {
          this.applySubmitError(error);
        },
        onSettled: () => {
          this.isSubmitting.set(false);
          this.#cdr.markForCheck();
        },
      });
      return;
    }

    const formData: CreateDutyPayload[] = [];
    const frequency = this.recurrenceFrequency();
    const family =
      this.#familyDuty.isEnabled() &&
      this.familyDutyAllowed() &&
      this.requiresParentParticipation();

    this.selectedKids().forEach((kid) => {
      const kidRecord = this.kids().find((k) => k.id === kid);
      const payload: CreateDutyPayload = {
        title: this.dutyForm.value.title,
        content: this.dutyForm.value.content,
        rewardGold: this.resolveRewardGold(this.dutyForm.value.rewardGold),
        rewardXp: this.resolveRewardXp(this.dutyForm.value.rewardXp),
        reciever: kid,
        recieverName: kidRecord
          ? this.#familyKids.displayName(kidRecord)
          : '',
        sender: this.user()?.id ?? '',
        senderName:
          `${this.user()?.personalData?.firstName ?? ''} ${this.user()?.personalData?.lastName ?? ''}`.trim(),
        requiresParentParticipation: family,
      };
      if (frequency !== 'NONE') {
        payload.recurrenceFrequency = frequency;
      }
      const parentNote = this.resolveParentNote(this.dutyForm.value.parentNote);
      if (parentNote) {
        payload.parentNote = parentNote;
      }
      formData.push(payload);
    });

    this.#userService.createDutiesBatch.mutate(formData, {
      onSuccess: async () => {
        try {
          if (this.saveAsTemplate()) {
            const savedFrequency = this.recurrenceFrequency();
            const savedFamily =
              this.#familyDuty.isEnabled() &&
              this.familyDutyAllowed() &&
              this.requiresParentParticipation();
            await this.#dutyTemplates.createTemplate.mutateAsync({
              name: String(this.dutyForm.value.templateName ?? '').trim(),
              items: [
                {
                  title: this.dutyForm.value.title,
                  content: this.dutyForm.value.content,
                  rewardGold: this.resolveRewardGold(
                    this.dutyForm.value.rewardGold,
                  ),
                  rewardXp: this.resolveRewardXp(this.dutyForm.value.rewardXp),
                  recurrenceFrequency:
                    savedFrequency !== 'NONE' ? savedFrequency : undefined,
                  requiresParentParticipation: savedFamily,
                  parentNote: this.resolveParentNote(
                    this.dutyForm.value.parentNote,
                  ),
                },
              ],
            });
          }
          this.dialogRef.close(formData);
        } catch (error: unknown) {
          this.applySubmitError(error);
        }
      },
      onError: (error: unknown) => {
        this.applySubmitError(error);
      },
      onSettled: () => {
        this.isSubmitting.set(false);
        this.#cdr.markForCheck();
      },
    });
  }

  private prefillEditMode(): void {
    const duty = this.editingDuty();
    if (!duty) return;

    this.dutyForm.patchValue({
      title: duty.title ?? '',
      content: duty.content ?? '',
      parentNote: duty.parentNote ?? '',
      rewardGold: duty.rewardGold > 0 ? duty.rewardGold : '',
      rewardXp:
        duty.rewardXp > 0 ? duty.rewardXp : DEFAULT_DUTY_REWARD_XP,
    });
    this.selectedKids.set([duty.reciever]);
    this.#initialKidSyncDone = true;
    const freq =
      (duty.recurrenceFrequency as DutyRecurrenceFrequency) || 'NONE';
    this.recurrenceFrequency.set(freq);
    this.requiresParentParticipation.set(!!duty.requiresParentParticipation);
    this.stopRecurrence.set(false);
  }

  private prefillCreateMode(): void {
    if (this.isEditMode()) {
      return;
    }
    const prefill = this.dialogData?.prefill;
    if (!prefill) {
      return;
    }

    this.dutyForm.patchValue({
      title: prefill.title ?? '',
      content: prefill.content ?? '',
      parentNote: prefill.parentNote ?? '',
    });

    if (prefill.requiresParentParticipation && this.#familyDuty.isEnabled() && this.familyDutyAllowed()) {
      this.requiresParentParticipation.set(true);
    }

    if (this.dialogData?.selectedKids?.length) {
      this.selectedKids.set([...this.dialogData.selectedKids]);
      this.#initialKidSyncDone = true;
    }
  }

  private syncInitialKidSelection(): void {
    if (
      this.isEditMode() ||
      this.#initialKidSyncDone ||
      this.selectedKids().length > 0
    ) {
      return;
    }

    const children = this.kids();
    if (children.length === 0) return;

    this.#initialKidSyncDone = true;

    const familyChildId = this.#family.selectedChildId();
    if (familyChildId && children.some((kid) => kid.id === familyChildId)) {
      this.selectedKids.set([familyChildId]);
      return;
    }

    this.selectedKids.set([children[0]!.id]);
  }

  goPlans(): void {
    this.dialogRef.close();
    void this.#router.navigate(['/profile'], {
      queryParams: { segment: 'subscriptions' },
    });
  }

  onPlanNoticeAction(): void {
    if (this.showDutyBoost()) {
      void this.buyDutyBoost();
      return;
    }
    this.goPlans();
  }

  async buyDutyBoost(): Promise<void> {
    if (this.boostLoading() || !this.showDutyBoost()) return;
    this.boostLoading.set(true);
    try {
      const result = await this.#userService.startAddonCheckout.mutateAsync(
        ADDON_CODE.DUTY_BOOST_5,
      );
      if (result?.url) {
        this.dialogRef.close();
        window.location.href = result.url;
        return;
      }
    } catch {
      // Keep dialog open; banner still shows View plans as fallback via secondary.
    } finally {
      this.boostLoading.set(false);
      this.#cdr.markForCheck();
    }
  }

  private applySubmitError(error: unknown): void {
    const apiError = readDutyApiError(error);
    const scopeKey = apiError?.meta?.scopeKey?.trim();
    if (apiError?.errorCode === 'PAYMENTS_QUOTA_EXCEEDED' && scopeKey) {
      const limit =
        typeof apiError.meta?.limit === 'number' && apiError.meta.limit > 0
          ? apiError.meta.limit
          : this.#userService.quotaScopeLimit(scopeKey);
      if (scopeKey === 'duties.create') {
        this.submitErrorKey.set(
          'app.components.addDutyDialog.plan.quotaCreate',
        );
        this.submitErrorParams.set(
          typeof limit === 'number' && limit > 0 ? { limit } : {},
        );
        this.#cdr.markForCheck();
        return;
      }
      if (scopeKey === 'duties.recurrence') {
        this.submitErrorKey.set(
          'app.components.addDutyDialog.plan.quotaRecurrence',
        );
        this.submitErrorParams.set(null);
        this.#cdr.markForCheck();
        return;
      }
      if (scopeKey === 'duties.family') {
        this.submitErrorKey.set(
          'app.components.addDutyDialog.plan.quotaFamily',
        );
        this.submitErrorParams.set(null);
        this.#cdr.markForCheck();
        return;
      }
      if (scopeKey === 'duties.routines') {
        this.submitErrorKey.set(
          'app.components.addDutyDialog.plan.quotaRoutines',
        );
        this.submitErrorParams.set(null);
        this.#cdr.markForCheck();
        return;
      }
    }
    this.submitErrorKey.set('app.components.addDutyDialog.submitError');
    this.submitErrorParams.set(null);
    this.#cdr.markForCheck();
  }

  private resolveRewardGold(raw: unknown): number {
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  private resolveRewardXp(raw: unknown): number {
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_DUTY_REWARD_XP;
  }

  private resolveParentNote(raw: unknown): string | undefined {
    if (typeof raw !== 'string') return undefined;
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    return trimmed.slice(0, DUTY_PARENT_NOTE_MAX_LENGTH);
  }

  private initForm(): void {
    this.dutyForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      content: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ],
      ],
      parentNote: ['', [Validators.maxLength(DUTY_PARENT_NOTE_MAX_LENGTH)]],
      rewardGold: [''],
      rewardXp: [DEFAULT_DUTY_REWARD_XP],
      templateName: [''],
    });
  }

  private loadValidationMessages(): void {
    this.translate
      .get([
        'app.components.addDutyDialog.nameRequired',
        'app.components.addDutyDialog.messageRequired',
        'app.components.addDutyDialog.parentNoteMaxLength',
        'app.components.addDutyDialog.saveTemplate.nameRequired',
        'app.components.addDutyDialog.saveTemplate.nameMinLength',
      ])
      .pipe(this.takeUntilDestroyed())
      .subscribe((translations) => {
        this.nameValidation.set({
          required: translations['app.components.addDutyDialog.nameRequired'],
        });
        this.messageValidation.set({
          required:
            translations['app.components.addDutyDialog.messageRequired'],
        });
        this.parentNoteValidation.set({
          maxlength:
            translations['app.components.addDutyDialog.parentNoteMaxLength'],
        });
        this.templateNameValidation.set({
          required:
            translations['app.components.addDutyDialog.saveTemplate.nameRequired'],
          minlength:
            translations['app.components.addDutyDialog.saveTemplate.nameMinLength'],
        });
      });
  }
}
